import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Snackbar,
  Alert,
  Autocomplete,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  Add,
  HourglassEmpty,
  Email,
  WhatsApp,
  AccessTime,
  AddPhotoAlternate,
  Close,
  Visibility,
  Phone as PhoneIcon,
  PublishedWithChanges,
  AutoFixHigh
} from '@mui/icons-material';
import { accommodationAPI, citiesAPI, districtsAPI, accommodationFacilitiesAPI, accommodationTypesAPI } from '../../services/api';
import browserGlobal from '../../utils/browserGlobal';
import { ApprovalStatus, ApprovalStatusLabels, APPROVAL_STATUS_FIELD } from '../../utils/enums';
import { useLanguage } from '../../context/LanguageContext';
import { getAdminUser } from '../../utils/authSessionStorage';
import { canManageAccommodation, canDeleteAccommodation } from '../../utils/roleUtils';

/**
 * Reads banner URLs from GET payload. Important: `pictures || bannerImage` is wrong when
 * `pictures` is [] — in JS [] is truthy, so `bannerImage` would never be read and the
 * edit form would send an empty `bannerImage`, wiping server images on save.
 */
function urlsFromPictureArray(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return [];
  const first = arr[0];
  if (typeof first === 'string') {
    return arr.map((u) => String(u).trim()).filter(Boolean);
  }
  return arr
    .map((p) =>
      typeof p === 'string' ? p : p?.imageUrl || p?.url || p?.ImageUrl || p?.Url || ''
    )
    .map((u) => String(u).trim())
    .filter(Boolean);
}

function urlsFromCommaString(v) {
  if (v == null || v === '') return [];
  if (typeof v === 'string') {
    return v.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

function extractUrlsFromSource(src) {
  if (src == null || src === '') return [];
  if (Array.isArray(src)) {
    if (src.length === 0) return [];
    return urlsFromPictureArray(src);
  }
  if (typeof src === 'string') {
    return urlsFromCommaString(src);
  }
  return [];
}

function extractAccommodationBannerUrls(fullData) {
  if (!fullData) return [];

  const orderedSources = [
    fullData.pictures,
    fullData.Pictures,
    fullData.imageUrls,
    fullData.ImageUrls,
    fullData.pictureUrls,
    fullData.PictureUrls,
    fullData.bannerImage,
    fullData.BannerImage
  ];

  for (const src of orderedSources) {
    const urls = extractUrlsFromSource(src);
    if (urls.length) return urls;
  }

  return [];
}

function mergeUniqueBannerUrls(...lists) {
  const seen = new Set();
  const out = [];
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const u of list) {
      const s = String(u || '').trim();
      if (s && !seen.has(s)) {
        seen.add(s);
        out.push(s);
      }
    }
  }
  return out;
}

const ACCOMMODATION_IMAGE_PAYLOAD_KEYS = new Set([
  'pictures',
  'Pictures',
  'bannerImage',
  'BannerImage',
  'imageUrls',
  'ImageUrls',
  'pictureUrls',
  'PictureUrls'
]);

function omitAccommodationImagePayloadFields(obj) {
  if (!obj || typeof obj !== 'object') return {};
  const out = { ...obj };
  ACCOMMODATION_IMAGE_PAYLOAD_KEYS.forEach((k) => {
    delete out[k];
  });
  return out;
}

function formatAccommodationAddress(address, houseNumber) {
  const base = address || '';
  if (!houseNumber) return base;
  return `${base}, ${houseNumber}`;
}

function getIsActiveFilterParam(isActiveFilter) {
  if (isActiveFilter === 'true') return true;
  if (isActiveFilter === 'false') return false;
  return undefined;
}

function isApprovedApprovalStatus(status) {
  return status === ApprovalStatus.APPROVED || status === 'APPROVED';
}

function isDeclinedApprovalStatus(status) {
  const normalized = String(status || '').toUpperCase();
  return normalized === 'REJECTED' || normalized === ApprovalStatus.DECLINE;
}

function getApprovalStatusLabel(status) {
  if (isApprovedApprovalStatus(status)) return ApprovalStatusLabels[ApprovalStatus.APPROVED];
  if (isDeclinedApprovalStatus(status)) return ApprovalStatusLabels[ApprovalStatus.DECLINE];
  if (status === ApprovalStatus.INACTIVE || status === 'INACTIVE') {
    return ApprovalStatusLabels[ApprovalStatus.INACTIVE];
  }
  return ApprovalStatusLabels[ApprovalStatus.PENDING];
}

function getApprovalStatusChipColor(status) {
  if (isApprovedApprovalStatus(status)) return 'success';
  if (isDeclinedApprovalStatus(status)) return 'error';
  if (status === ApprovalStatus.INACTIVE || status === 'INACTIVE') return 'default';
  return 'warning';
}

function getAccommodationSubmitLabel(isSubmitting, editMode, translate) {
  if (isSubmitting) return translate('common.saving');
  if (editMode) return translate('common.update');
  return translate('common.add');
}

const SLASH_DATE_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const ISO_DATE_INPUT_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const POSTAL_CODE_PATTERN = /^\d{5}$/;

function isEmptyFormValue(value) {
  return value == null || value === '';
}

function formatDateInputParts(year, month, day) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function isValidCalendarDate(day, month, year) {
  return day >= 1 && day <= 31 && month >= 1 && month <= 12 && year > 0;
}

function parseSlashDateForInput(cleanedDate) {
  const match = SLASH_DATE_PATTERN.exec(cleanedDate);
  if (!match) return '';

  const dayNum = Number.parseInt(match[1], 10);
  const monthNum = Number.parseInt(match[2], 10);
  const yearNum = Number.parseInt(match[3], 10);

  if (isValidCalendarDate(dayNum, monthNum, yearNum)) {
    return formatDateInputParts(yearNum, monthNum, dayNum);
  }

  return '';
}

function parseIsoLikeDateForInput(cleanedDate) {
  try {
    const date = new Date(cleanedDate);
    if (Number.isNaN(date.getTime())) return '';
    return formatDateInputParts(date.getFullYear(), date.getMonth() + 1, date.getDate());
  } catch (error) {
    console.error('Error parsing date:', cleanedDate, error);
    return '';
  }
}

function parseDateForInput(dateString) {
  if (!dateString) return '';

  const cleanedDate = String(dateString).trim();
  if (ISO_DATE_INPUT_PATTERN.test(cleanedDate)) {
    return cleanedDate;
  }

  const slashDate = parseSlashDateForInput(cleanedDate);
  if (slashDate) return slashDate;

  if (cleanedDate.includes('T') || cleanedDate.includes(' ')) {
    const isoDate = parseIsoLikeDateForInput(cleanedDate);
    if (isoDate) return isoDate;
  }

  if (!cleanedDate.includes('/')) {
    const fallbackDate = parseIsoLikeDateForInput(cleanedDate);
    if (fallbackDate) return fallbackDate;
  }

  console.warn('Could not parse date format:', cleanedDate);
  return '';
}

function getTodayDateString() {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function getLatitudeFieldError(value) {
  if (isEmptyFormValue(value)) return 'Latitude is required';
  const latValue = Number.parseFloat(value);
  if (Number.isNaN(latValue)) return 'Latitude must be a valid number';
  if (latValue < -90 || latValue > 90) return 'Latitude must be between -90 and 90';
  return '';
}

function getLongitudeFieldError(value) {
  if (isEmptyFormValue(value)) return 'Longitude is required';
  const lngValue = Number.parseFloat(value);
  if (Number.isNaN(lngValue)) return 'Longitude must be a valid number';
  if (lngValue < -180 || lngValue > 180) return 'Longitude must be between -180 and 180';
  return '';
}

function getEmailFieldError(value) {
  if (isEmptyFormValue(value)) return '';
  if (!EMAIL_PATTERN.test(String(value).trim())) return 'Please enter a valid email address';
  return '';
}



function parseRawAccommodationText(text, cities = [], districts = [], facilitiesList = [], accommodationTypes = []) {
  if (!text || !text.trim()) return {};

  const cleanText = text.trim();
  const lines = cleanText.split('\n').map((l) => l.trim()).filter(Boolean);
  const result = {};

  // 1. Email
  const emailMatch = cleanText.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/i);
  if (emailMatch) {
    result.email = emailMatch[0];
  }

  // 2. Phone / WhatsApp
  const phoneMatch = cleanText.match(/(?:phone|tel|mobile|whatsapp|contact|call)?:?\s*(\+?\d[\d\s\-()]{7,15}\d)/i) ||
                     cleanText.match(/(\+49[\d\s\-]{8,15}|\b01[567]\d[\d\s\-]{7,12}\b)/);
  if (phoneMatch) {
    const p = phoneMatch[1] || phoneMatch[0];
    const cleanedP = p.replace(/[^\d+]/g, '');
    if (cleanedP.length >= 7) {
      result.phone = p.trim();
      result.whatsAppNumber = p.trim();
    }
  }

  // 3. Rent / Price
  const rentMatch = cleanText.match(/(?:rent|price|kaltmiete|warmmiete|cost|miete)?:?\s*(?:€|eur)?\s*(\d{2,5})\s*(?:€|eur|\/month|per month|pm)?\b/i) ||
                    cleanText.match(/(\d{3,5})\s*(?:€|eur)/i);
  if (rentMatch) {
    const val = parseFloat(rentMatch[1]);
    if (!isNaN(val) && val > 0) {
      result.rent = val;
    }
  }

  // 4. Deposit
  const depositMatch = cleanText.match(/(?:deposit|kaution)?:?\s*(?:€|eur)?\s*(\d{2,5})\s*(?:€|eur)?\b/i);
  if (depositMatch && depositMatch[1]) {
    const val = parseFloat(depositMatch[1]);
    if (!isNaN(val) && val > 0 && val !== result.rent) {
      result.deposit = val;
    }
  }

  // 5. Postal Code & Address
  const postalCodeMatch = cleanText.match(/\b(\d{5})\b/);
  if (postalCodeMatch) {
    result.postalCode = postalCodeMatch[1];
  }

  const addressLine = lines.find((l) =>
    /(?:street|str\.|straße|strasse|way|allee|platz|road|address|location:)/i.test(l)
  );
  if (addressLine) {
    const addrClean = addressLine.replace(/^(address|location):\s*/i, '').trim();
    const houseNoMatch = addrClean.match(/\b(\d+[a-c]?)\b$/i);
    if (houseNoMatch) {
      result.houseNumber = houseNoMatch[1];
      result.address = addrClean.replace(/\b\d+[a-c]?\b$/i, '').trim().replace(/,\s*$/, '');
    } else {
      result.address = addrClean;
    }
  }

  // 6. Living Area
  const sqmMatch = cleanText.match(/(\d{1,4})\s*(?:sqm|m²|m2|square meters)/i) ||
                   cleanText.match(/(?:living area|area|size):?\s*(\d{1,4})/i);
  if (sqmMatch) {
    const area = parseFloat(sqmMatch[1]);
    if (!isNaN(area) && area > 0) {
      result.livingArea = area;
    }
  }

  // 7. Rooms / Baths / Beds / Persons
  const bedsMatch = cleanText.match(/(\d+)\s*(?:bed|bedroom|beds|bedrooms)/i);
  if (bedsMatch) {
    result.numberOfBeds = parseInt(bedsMatch[1], 10) || 1;
  }

  const bathsMatch = cleanText.match(/(\d+)\s*(?:bath|bathroom|baths|bathrooms|total rooms|rooms)/i);
  if (bathsMatch) {
    result.numberOfBaths = parseInt(bathsMatch[1], 10) || 1;
  }

  const personsMatch = cleanText.match(/(\d+)\s*(?:person|persons|people|tenant|tenants|occupant|occupants)/i);
  if (personsMatch) {
    result.totalPerson = parseInt(personsMatch[1], 10) || 1;
    result.spacesAvailable = parseInt(personsMatch[1], 10) || 1;
  }

  // 8. Room Type
  if (/sharing|shared|wg|flatshare|room in a/i.test(cleanText)) {
    result.roomType = 'Sharing';
  } else if (/private room|single room|1 room|bedroom in/i.test(cleanText)) {
    result.roomType = 'Private Room';
  } else if (/entire|full apartment|whole apartment|studio|1bhk|2bhk|3bhk/i.test(cleanText)) {
    result.roomType = 'Entire Apartment';
  }

  // 9. Furnishing Status
  if (/fully furnished|fully-furnished|furnished/i.test(cleanText)) {
    result.furnishingStatus = 'Fully Furnished';
  } else if (/semi furnished|semi-furnished|partly furnished/i.test(cleanText)) {
    result.furnishingStatus = 'Semi-Furnished';
  } else if (/unfurnished|not furnished/i.test(cleanText)) {
    result.furnishingStatus = 'Unfurnished';
  }

  // 10. Gender Preference
  if (/female only|females only|women only|girls only/i.test(cleanText)) {
    result.genderPreference = 'Female';
  } else if (/male only|males only|men only|boys only/i.test(cleanText)) {
    result.genderPreference = 'Male';
  } else if (/any gender|no preference|coed|mixed|all welcome/i.test(cleanText)) {
    result.genderPreference = 'Any';
  }

  // 11. Booleans
  if (/anmeldung (?:possible|allowed|yes|available)|anmeldung/i.test(cleanText) && !/no anmeldung|anmeldung not/i.test(cleanText)) {
    result.anmeldungPossible = true;
  }
  if (/utilities (?:included|inc)|warmmiete|inclusive of bills|bills included/i.test(cleanText)) {
    result.utilitiesIncluded = true;
  }
  if (/commission free|no commission|provisionsfrei|0% commission/i.test(cleanText)) {
    result.commissionFree = true;
  }
  if (/contract (?:provided|available)|mietvertrag|lease agreement/i.test(cleanText)) {
    result.contractProvided = true;
  }

  // 12. Available From Date
  const dateMatch = cleanText.match(/\b(\d{4}-\d{2}-\d{2})\b/) ||
                    cleanText.match(/\b(\d{1,2})[\/\.](\d{1,2})[\/\.](\d{4})\b/);
  if (dateMatch) {
    if (dateMatch[0].includes('-')) {
      result.availableFrom = dateMatch[0];
    } else {
      const d = dateMatch[1].padStart(2, '0');
      const m = dateMatch[2].padStart(2, '0');
      const y = dateMatch[3];
      result.availableFrom = `${y}-${m}-${d}`;
    }
  } else if (/immediately|now|asap|from today/i.test(cleanText)) {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    result.availableFrom = `${yyyy}-${mm}-${dd}`;
  }

  // 13. Title & Description
  const titleLine = lines.find((l) => /^title:\s*/i.test(l));
  if (titleLine) {
    result.title = titleLine.replace(/^title:\s*/i, '').trim();
  } else if (lines.length > 0) {
    result.title = lines[0].slice(0, 100).replace(/^(title|name|accommodation):\s*/i, '');
  }
  result.description = cleanText;

  // 14. Matching City
  if (Array.isArray(cities) && cities.length > 0) {
    const foundCity = cities.find((c) => {
      const cityName = (c.name || c.cityName || '').toLowerCase();
      return cityName && cleanText.toLowerCase().includes(cityName);
    });
    if (foundCity) {
      result.cityId = foundCity.id;
    }
  }

  // 15. Matching District
  if (Array.isArray(districts) && districts.length > 0) {
    const foundDistrict = districts.find((d) => {
      const districtName = (d.name || d.districtName || '').toLowerCase();
      return districtName && cleanText.toLowerCase().includes(districtName);
    });
    if (foundDistrict) {
      result.districtId = foundDistrict.id;
      result.district = foundDistrict.name;
    }
  }

  // 16. Matching Accommodation Type
  if (Array.isArray(accommodationTypes) && accommodationTypes.length > 0) {
    const foundType = accommodationTypes.find((t) => {
      const typeName = (t.name || t.title || '').toLowerCase();
      return typeName && cleanText.toLowerCase().includes(typeName);
    });
    if (foundType) {
      result.accommodationTypeId = foundType.id;
      result.typeId = foundType.id;
    }
  }

  // 17. Matching Facilities
  if (Array.isArray(facilitiesList) && facilitiesList.length > 0) {
    const matchedFacilities = [];
    facilitiesList.forEach((fac) => {
      const facName = (fac.title_EN || fac.name_EN || fac.title || fac.name || '').toLowerCase();
      if (facName && cleanText.toLowerCase().includes(facName)) {
        matchedFacilities.push(fac.title_EN || fac.name_EN || fac.title || fac.name);
      }
    });
    if (matchedFacilities.length > 0) {
      result.facilities = matchedFacilities;
    }
  }

  return result;
}

function getPostalCodeFieldError(value, translate) {
  const postalCodeStr = String(value).trim();
  if (postalCodeStr === '') return '';
  if (!POSTAL_CODE_PATTERN.test(postalCodeStr)) {
    return translate('contacts.errPostalDigits5') || 'Postal code must be a 5-digit number';
  }
  return '';
}

function getAvailableFromFieldError(value, translate) {
  if (!value?.trim()) return translate('contacts.errFieldRequired');
  if (value < getTodayDateString()) {
    return translate('accommodation.errAvailableFromPast') || 'Date cannot be in the past';
  }
  return '';
}

const ACCOMMODATION_EMPTY_ERRORS = {
  title: '',
  description: '',
  cityId: '',
  districtId: '',
  accommodationTypeId: '',
  address: '',
  houseNumber: '',
  phone: '',
  availableFrom: '',
  facilities: '',
  latitude: '',
  longitude: '',
  email: '',
  rent: '',
  deposit: '',
  postalCode: '',
  livingArea: '',
  numberOfBaths: '',
  totalPerson: '',
};

function getSpacesAvailableLabel(roomType) {
  if (roomType === 'Sharing') {
    return 'Spaces Available';
  }
  return 'Occupants';
}

const ACCOMMODATION_FIELD_ORDER = [
  'title',
  'description',
  'cityId',
  'districtId',
  'accommodationTypeId',
  'facilities',
  'address',
  'houseNumber',
  'postalCode',
  'email',
  'whatsAppNumber',
  'phone',
  'availableFrom',
  'roomType',
  'spacesAvailable',
  'rent',
  'deposit',
  'livingArea',
  'numberOfBaths',
  'totalPerson',
  'genderPreference',
  'furnishingStatus',
  'whatYouGet',
  'ownerName',
];

const ACCOMMODATION_LIVE_FIELD_VALIDATORS = {
  latitude: (value) => getLatitudeFieldError(value),
  longitude: (value) => getLongitudeFieldError(value),
  email: (value, translate) => getEmailFormError(value, translate),
  rent: (value, translate) => getRentFormError(value, translate),
  deposit: (value, translate) => getDepositFormError(value, translate),
  postalCode: (value, translate) => getPostalCodeFieldError(value, translate),
  availableFrom: (value, translate) => getAvailableFromFieldError(value, translate),
  houseNumber: (value, translate) => getRequiredTextFormError(value, translate),
  phone: () => '',
  livingArea: (value, translate) => getPositiveNumberFormError(value, translate, 'Living Area'),
  numberOfBaths: (value, translate) => getPositiveNumberFormError(value, translate, 'Total Rooms'),
  totalPerson: (value, translate) => getPositiveNumberFormError(value, translate, 'Total Person'),
  accommodationTypeId: (value, translate) => getRequiredTextFormError(value, translate),
};

function getRentFormError(value, translate) {
  if (isEmptyFormValue(value)) return translate('contacts.errFieldRequired');
  const num = Number.parseFloat(value);
  if (Number.isNaN(num) || num < 0) return translate('accommodation.toastRent');
  return '';
}

function getDepositFormError(value, translate) {
  if (isEmptyFormValue(value)) return translate('contacts.errFieldRequired');
  const num = Number.parseFloat(value);
  if (Number.isNaN(num) || num < 0) return translate('accommodation.toastDeposit');
  return '';
}

function getLatitudeFormError(value, translate) {
  if (isEmptyFormValue(value)) return '';
  const num = Number.parseFloat(value);
  if (Number.isNaN(num) || num < -90 || num > 90) return translate('accommodation.toastLatRange');
  return '';
}

function getLongitudeFormError(value, translate) {
  if (isEmptyFormValue(value)) return '';
  const num = Number.parseFloat(value);
  if (Number.isNaN(num) || num < -180 || num > 180) return translate('accommodation.toastLngRange');
  return '';
}

function getFacilitiesFormError(facilities, translate) {
  if (!facilities) return translate('contacts.errFieldRequired');
  if (Array.isArray(facilities) && facilities.length === 0) return translate('contacts.errFieldRequired');
  if (typeof facilities === 'string' && !facilities.trim()) return translate('contacts.errFieldRequired');
  return '';
}

function getEmailFormError(email, translate) {
  if (!email || !email.trim()) return '';
  if (!EMAIL_PATTERN.test(email.trim())) return translate('accommodation.toastEmail');
  return '';
}

function getPostalCodeFormError(postalCode, translate) {
  if (!postalCode?.trim()) return translate('contacts.errFieldRequired');
  return getPostalCodeFieldError(postalCode, translate);
}

function getRequiredTextFormError(value, translate) {
  if (!value || !String(value).trim()) return translate('contacts.errFieldRequired') || 'Required';
  return '';
}

function getPositiveNumberFormError(value, translate, labelName) {
  if (isEmptyFormValue(value)) return translate('contacts.errFieldRequired') || 'Required';
  const num = Number.parseFloat(value);
  if (Number.isNaN(num) || num <= 0) return `${labelName} must be greater than 0`;
  return '';
}

function validateAccommodationFormFields(c, translate) {
  const addressStr = c.address?.trim() || '';
  let addressError = '';
  if (!addressStr) {
    addressError = translate('contacts.errFieldRequired') || 'Required';
  } else if (!c.latitude || !c.longitude) {
    addressError = 'Please select address from auto-fetch suggestions';
  }

  return {
    ...ACCOMMODATION_EMPTY_ERRORS,
    title: c.title?.trim() ? '' : translate('contacts.errFieldRequired'),
    description: c.description?.trim() ? '' : translate('contacts.errFieldRequired'),
    cityId: c.cityId ? '' : translate('contacts.errFieldRequired'),
    districtId: c.districtId ? '' : translate('contacts.errFieldRequired'),
    accommodationTypeId: (c.accommodationTypeId || c.typeId) ? '' : translate('contacts.errFieldRequired'),
    address: addressError,
    houseNumber: getRequiredTextFormError(c.houseNumber, translate),
    phone: '',
    postalCode: getPostalCodeFormError(c.postalCode, translate),
    email: getEmailFormError(c.email, translate),
    rent: getRentFormError(c.rent, translate),
    deposit: getDepositFormError(c.deposit, translate),
    livingArea: getPositiveNumberFormError(c.livingArea, translate, 'Living Area'),
    numberOfBaths: getPositiveNumberFormError(c.numberOfBaths, translate, 'Total Rooms'),
    totalPerson: getPositiveNumberFormError(c.totalPerson, translate, 'Total Person'),
    availableFrom: getAvailableFromFieldError(c.availableFrom, translate),
    facilities: getFacilitiesFormError(c.facilities, translate),
    latitude: getLatitudeFormError(c.latitude, translate),
    longitude: getLongitudeFormError(c.longitude, translate),
  };
}

function normalizeAccommodationsPayload(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (typeof payload === 'object') return [payload];
  return [];
}

function formatAvailableFromForApi(availableFrom) {
  if (availableFrom && availableFrom.includes('-')) {
    const [year, month, day] = availableFrom.split('-');
    return `${day}/${month}/${year}`;
  }
  return availableFrom || '';
}

const ACCOMMODATION_FORM_DATA_SKIP_KEYS = new Set([
  'Pictures',
  'existingBannerImages',
  'picturesRemove',
  'availableFrom',
  'id',
  'cityId',
  'districtId',
  'latitude',
  'longitude',
  'rent',
  'deposit',
  'bannerImage',
  'BannerImage',
  'pictures',
  'imageUrls',
  'ImageUrls',
  'pictureUrls',
  'PictureUrls',
  'anmeldungPossible',
  'AnmeldungPossible',
  'genderPreference',
  'GenderPreference',
  'furnishingStatus',
  'FurnishingStatus',
  'utilitiesIncluded',
  'UtilitiesIncluded',
  'commissionFree',
  'CommissionFree',
  'contractProvided',
  'ContractProvided',
  'spacesAvailable',
  'SpacesAvailable',
  'roomType',
  'RoomType',
  'livingArea',
  'LivingArea',
  'numberOfBeds',
  'NumberOfBeds',
  'numberOfBaths',
  'NumberOfBaths',
  'totalPerson',
  'TotalPerson',
  'totalPersons',
  'TotalPersons',
  'phone',
  'Phone',
  'whatYouGet',
  'WhatYouGet',
]);

function appendAccommodationFormValue(formData, key, raw) {
  if (raw == null) {
    formData.append(key, '');
    return;
  }
  if (Array.isArray(raw)) {
    formData.append(key, raw.join(','));
    return;
  }
  if (raw instanceof Date) {
    formData.append(key, raw.toISOString());
    return;
  }
  if (typeof raw === 'object') return;
  formData.append(key, raw);
}

function toAccommodationFormNumber(value) {
  return value === '' || value == null ? 0 : Number(value);
}

function appendAccommodationBannerFields(formDataToSend, keepUrls, picturesRemove, editMode) {
  keepUrls.forEach((url) => {
    formDataToSend.append('pictures', url);
  });

  const remainingBanner = keepUrls.join(',');
  if (remainingBanner) {
    formDataToSend.append('bannerImage', remainingBanner);
    formDataToSend.append('BannerImage', remainingBanner);
    formDataToSend.append('ExistingPictures', remainingBanner);
    formDataToSend.append('existingPictures', remainingBanner);
  } else if (editMode && picturesRemove.length > 0) {
    formDataToSend.append('bannerImage', '');
    formDataToSend.append('BannerImage', '');
    formDataToSend.append('ExistingPictures', '');
  }

  picturesRemove.forEach((url) => {
    formDataToSend.append('picturesRemove', url);
    formDataToSend.append('PicturesRemove', url);
  });
}

function normalizeCommaSeparated(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(s => typeof s === 'string' ? s.replace(/‚/g, ',') : s).filter(Boolean);
  if (typeof value === 'string') {
    if (!value.trim()) return [];
    return value.split(',').map((s) => s.replace(/‚/g, ',').trim()).filter(Boolean);
  }
  return value || [];
}

function buildAccommodationFormData(currentAccommodation, editMode, apiDate) {
  const formDataToSend = new FormData();

  Object.keys(currentAccommodation).forEach((key) => {
    if (ACCOMMODATION_FORM_DATA_SKIP_KEYS.has(key)) {
      return;
    }
    appendAccommodationFormValue(formDataToSend, key, currentAccommodation[key]);
  });

  const accId = editMode ? (Number(currentAccommodation.id) || 0) : 0;
  formDataToSend.append('id', accId);
  formDataToSend.append('Id', accId);
  formDataToSend.append('cityId', toAccommodationFormNumber(currentAccommodation.cityId));
  formDataToSend.append('districtId', toAccommodationFormNumber(currentAccommodation.districtId));
  formDataToSend.append('accommodationTypeId', toAccommodationFormNumber(currentAccommodation.accommodationTypeId || currentAccommodation.typeId));
  formDataToSend.append('AccommodationTypeId', toAccommodationFormNumber(currentAccommodation.accommodationTypeId || currentAccommodation.typeId));
  formDataToSend.append('typeId', toAccommodationFormNumber(currentAccommodation.accommodationTypeId || currentAccommodation.typeId));
  formDataToSend.append('TypeId', toAccommodationFormNumber(currentAccommodation.accommodationTypeId || currentAccommodation.typeId));
  formDataToSend.append('latitude', toAccommodationFormNumber(currentAccommodation.latitude));
  formDataToSend.append('longitude', toAccommodationFormNumber(currentAccommodation.longitude));
  formDataToSend.append('rent', toAccommodationFormNumber(currentAccommodation.rent));
  formDataToSend.append('deposit', toAccommodationFormNumber(currentAccommodation.deposit));
  formDataToSend.append('availableFrom', apiDate);
  formDataToSend.append(APPROVAL_STATUS_FIELD, currentAccommodation.approvalStatus || ApprovalStatus.PENDING);
  formDataToSend.append('anmeldungPossible', Boolean(currentAccommodation.anmeldungPossible));
  formDataToSend.append('AnmeldungPossible', Boolean(currentAccommodation.anmeldungPossible));
  formDataToSend.append('genderPreference', currentAccommodation.genderPreference || 'Male');
  formDataToSend.append('GenderPreference', currentAccommodation.genderPreference || 'Male');
  formDataToSend.append('furnishingStatus', currentAccommodation.furnishingStatus || 'Fully Furnished');
  formDataToSend.append('FurnishingStatus', currentAccommodation.furnishingStatus || 'Fully Furnished');
  formDataToSend.append('utilitiesIncluded', Boolean(currentAccommodation.utilitiesIncluded));
  formDataToSend.append('UtilitiesIncluded', Boolean(currentAccommodation.utilitiesIncluded));
  formDataToSend.append('commissionFree', Boolean(currentAccommodation.commissionFree));
  formDataToSend.append('CommissionFree', Boolean(currentAccommodation.commissionFree));
  formDataToSend.append('contractProvided', Boolean(currentAccommodation.contractProvided));
  formDataToSend.append('ContractProvided', Boolean(currentAccommodation.contractProvided));
  formDataToSend.append('spacesAvailable', toAccommodationFormNumber(currentAccommodation.spacesAvailable));
  formDataToSend.append('SpacesAvailable', toAccommodationFormNumber(currentAccommodation.spacesAvailable));
  formDataToSend.append('roomType', currentAccommodation.roomType || '');
  formDataToSend.append('RoomType', currentAccommodation.roomType || '');
  formDataToSend.append('livingArea', toAccommodationFormNumber(currentAccommodation.livingArea));
  formDataToSend.append('LivingArea', toAccommodationFormNumber(currentAccommodation.livingArea));
  formDataToSend.append('numberOfBeds', toAccommodationFormNumber(currentAccommodation.numberOfBeds));
  formDataToSend.append('NumberOfBeds', toAccommodationFormNumber(currentAccommodation.numberOfBeds));
  formDataToSend.append('numberOfBaths', toAccommodationFormNumber(currentAccommodation.numberOfBaths));
  formDataToSend.append('NumberOfBaths', toAccommodationFormNumber(currentAccommodation.numberOfBaths));
  formDataToSend.append('totalPerson', toAccommodationFormNumber(currentAccommodation.totalPerson));
  formDataToSend.append('TotalPerson', toAccommodationFormNumber(currentAccommodation.totalPerson));
  formDataToSend.append('totalPersons', toAccommodationFormNumber(currentAccommodation.totalPerson));
  formDataToSend.append('TotalPersons', toAccommodationFormNumber(currentAccommodation.totalPerson));
  formDataToSend.append('phone', currentAccommodation.phone || '');
  formDataToSend.append('Phone', currentAccommodation.phone || '');

  const filteredWhatYouGet = (currentAccommodation.whatYouGet || [])
    .map((item) => String(item || '').replace(/,/g, '‚').trim())
    .filter(Boolean);

  if (filteredWhatYouGet.length > 0) {
    filteredWhatYouGet.forEach((item) => {
      formDataToSend.append('whatYouGet', item);
      formDataToSend.append('WhatYouGet', item);
    });
  } else {
    formDataToSend.append('whatYouGet', '');
    formDataToSend.append('WhatYouGet', '');
  }

  const keepUrls = (currentAccommodation.existingBannerImages || [])
    .map((u) => String(u).trim())
    .filter(Boolean);

  currentAccommodation.Pictures.forEach((file) => {
    formDataToSend.append('Pictures', file);
  });

  appendAccommodationBannerFields(
    formDataToSend,
    keepUrls,
    currentAccommodation.picturesRemove || [],
    editMode,
  );

  return formDataToSend;
}


function getApprovalNoteTypographyColor(status) {
  if (isDeclinedApprovalStatus(status)) return 'error';
  return 'textSecondary';
}

function getApprovalDialogIcon(status) {
  if (isApprovedApprovalStatus(status)) return <CheckCircle color="success" />;
  if (isDeclinedApprovalStatus(status)) return <Cancel color="error" />;
  return <HourglassEmpty color="warning" />;
}

function getApprovalDialogTitlePrefix(status, translate) {
  if (isApprovedApprovalStatus(status)) return translate('accommodation.approveTitle');
  if (isDeclinedApprovalStatus(status)) return translate('accommodation.rejectTitle');
  return translate('accommodation.resetTitle');
}

function getApprovalDialogButtonColor(status) {
  if (isApprovedApprovalStatus(status)) return 'success';
  if (isDeclinedApprovalStatus(status)) return 'error';
  return 'warning';
}

function getApprovalNoteFieldLabel(status, translate) {
  if (isDeclinedApprovalStatus(status)) return translate('accommodation.fieldRejectionNote');
  return translate('accommodation.fieldApprovalNote');
}

function getApprovalNotePlaceholder(status, translate) {
  if (isDeclinedApprovalStatus(status)) return translate('accommodation.placeholderReject');
  return translate('accommodation.placeholderOptional');
}

function AccommodationApprovalActions({ accommodation, isSubmitting, onOpenApprovalDialog }) {
  const rowActive = accommodation.isActive === true || accommodation.IsActive === true;
  if (!rowActive) return null;

  return (
    <IconButton
      color="warning"
      onClick={() => onOpenApprovalDialog(accommodation, accommodation.approvalStatus)}
      size="small"
      title="Change Status"
      disabled={isSubmitting}
    >
      <PublishedWithChanges />
    </IconButton>
  );
}

AccommodationApprovalActions.propTypes = {
  accommodation: PropTypes.shape({
    isActive: PropTypes.bool,
    IsActive: PropTypes.bool,
    approvalStatus: PropTypes.string,
  }).isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onOpenApprovalDialog: PropTypes.func.isRequired,
};

const Accommodation = () => {
  /** Avoid duplicate cities bootstrap when React Strict Mode runs effects twice in dev */
  const initialCitiesBootstrapRef = useRef(false);

  const { t } = useLanguage();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));
  const adminUser = getAdminUser();
  const canAddEdit = canManageAccommodation(adminUser?.role);
  const canDelete = canDeleteAccommodation(adminUser?.role);

  const [accommodations, setAccommodations] = useState({ payload: [], totalRecord: 0 });
  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState({ text: '', type: 'info' });
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [accommodationTypes, setAccommodationTypes] = useState([]);

  useEffect(() => {
    const fetchAccommodationTypes = async () => {
      try {
        const response = await accommodationTypesAPI.getAccommodationTypes(0, 1000);
        if (response?.data?.success === true) {
          setAccommodationTypes(response.data.payload || []);
        } else if (Array.isArray(response?.data?.payload)) {
          setAccommodationTypes(response.data.payload);
        } else if (Array.isArray(response?.data)) {
          setAccommodationTypes(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch accommodation types:', error);
      }
    };
    fetchAccommodationTypes();
  }, []);
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState('');
  const [approvalNote, setApprovalNote] = useState('');
  const [accommodationToApprove, setAccommodationToApprove] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCity, setSelectedCity] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  /** Title filter: debounced before calling GET /Accomodations?title=… */
  const [titleSearch, setTitleSearch] = useState('');
  const [debouncedTitle, setDebouncedTitle] = useState('');
  const debouncedTitleRef = useRef(debouncedTitle);
  useEffect(() => {
    debouncedTitleRef.current = debouncedTitle;
  }, [debouncedTitle]);
  /** '' = all, 'true' / 'false' → GET ?isActive=… */
  const [isActiveFilter, setIsActiveFilter] = useState('');
  const [errors, setErrors] = useState(() => ({ ...ACCOMMODATION_EMPTY_ERRORS }));
  const [autoFillText, setAutoFillText] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);

  const dialogContentRef = useRef(null);

  const scrollToField = (fieldKey) => {
    setTimeout(() => {
      if (!dialogContentRef.current) return;
      const targetElement =
        dialogContentRef.current.querySelector(`[data-field="${fieldKey}"]`) ||
        dialogContentRef.current.querySelector(`[name="${fieldKey}"]`);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const inputEl = targetElement.querySelector('input, textarea, select, [tabindex="0"]') || targetElement;
        if (inputEl && typeof inputEl.focus === 'function') {
          try {
            inputEl.focus({ preventScroll: true });
          } catch (e) {}
        }
      }
    }, 100);
  };

  const handleAutoFillFromText = async () => {
    if (!autoFillText.trim()) return;

    setIsAiLoading(true);
    let parsed = {};

    try {
      const response = await accommodationAPI.aiAutofill({
        rawText: autoFillText.trim()
      });

      const apiData = response?.data?.payload ?? response?.data?.data ?? response?.data;
      if (apiData && typeof apiData === 'object') {
        parsed = {
          title: apiData.title || apiData.Title || apiData.name || apiData.Name,
          description: apiData.description || apiData.Description,
          address: apiData.address || apiData.Address,
          houseNumber: apiData.houseNumber || apiData.HouseNumber,
          postalCode: apiData.postalCode || apiData.PostalCode,
          cityId: apiData.cityId || apiData.CityId,
          districtId: apiData.districtId || apiData.DistrictId,
          district: apiData.district || apiData.District,
          rent: apiData.rent ?? apiData.Rent,
          deposit: apiData.deposit ?? apiData.Deposit,
          email: apiData.email || apiData.Email,
          phone: apiData.phone || apiData.Phone || apiData.whatsAppNumber || apiData.WhatsAppNumber,
          whatsAppNumber: apiData.whatsAppNumber || apiData.WhatsAppNumber || apiData.phone || apiData.Phone,
          livingArea: apiData.livingArea ?? apiData.LivingArea,
          numberOfBeds: apiData.numberOfBeds ?? apiData.NumberOfBeds,
          numberOfBaths: apiData.numberOfBaths ?? apiData.NumberOfBaths,
          totalPerson: apiData.totalPerson ?? apiData.TotalPerson ?? apiData.totalPersons ?? apiData.TotalPersons,
          roomType: apiData.roomType || apiData.RoomType,
          furnishingStatus: apiData.furnishingStatus || apiData.FurnishingStatus,
          genderPreference: apiData.genderPreference || apiData.GenderPreference,
          availableFrom: apiData.availableFrom ? parseDateForInput(apiData.availableFrom) : undefined,
          accommodationTypeId: apiData.accommodationTypeId || apiData.AccommodationTypeId || apiData.typeId || apiData.TypeId,
          facilities: apiData.facilities ? (Array.isArray(apiData.facilities) ? apiData.facilities : String(apiData.facilities).split(',').map(s => s.trim()).filter(Boolean)) : undefined,
          anmeldungPossible: apiData.anmeldungPossible ?? apiData.AnmeldungPossible,
          utilitiesIncluded: apiData.utilitiesIncluded ?? apiData.UtilitiesIncluded,
          commissionFree: apiData.commissionFree ?? apiData.CommissionFree,
          contractProvided: apiData.contractProvided ?? apiData.ContractProvided,
        };
      }
    } catch (err) {
      console.warn('AI Autofill API call fallback to client parser:', err);
    } finally {
      setIsAiLoading(false);
    }

    const clientParsed = parseRawAccommodationText(autoFillText, cities, districts, facilities, accommodationTypes);
    const merged = {
      ...clientParsed,
      ...Object.fromEntries(Object.entries(parsed).filter(([_, v]) => v !== undefined && v !== '' && v !== null))
    };

    const populatedFields = [];

    if (merged.cityId) {
      await fetchDistricts(merged.cityId);
      populatedFields.push('City');
    }

    setCurrentAccommodation((prev) => {
      const next = { ...prev };
      Object.keys(merged).forEach((key) => {
        if (merged[key] !== undefined && merged[key] !== '' && merged[key] !== null) {
          next[key] = merged[key];
          if (key !== 'cityId') {
            const labelMap = {
              title: 'Title',
              description: 'Description',
              address: 'Address',
              houseNumber: 'House Number',
              postalCode: 'Postal Code',
              districtId: 'District',
              email: 'Email',
              phone: 'Phone',
              rent: 'Rent',
              deposit: 'Deposit',
              livingArea: 'Living Area',
              numberOfBeds: 'Beds',
              numberOfBaths: 'Baths',
              totalPerson: 'Total Persons',
              roomType: 'Room Type',
              furnishingStatus: 'Furnishing',
              genderPreference: 'Gender Preference',
              availableFrom: 'Available From',
              facilities: 'Facilities',
              accommodationTypeId: 'Accommodation Type',
              anmeldungPossible: 'Anmeldung',
              utilitiesIncluded: 'Utilities',
              commissionFree: 'Commission Free',
              contractProvided: 'Contract Provided'
            };
            if (labelMap[key] && !populatedFields.includes(labelMap[key])) {
              populatedFields.push(labelMap[key]);
            }
          }
        }
      });
      return next;
    });

    setErrors((prev) => {
      const nextErr = { ...prev };
      Object.keys(merged).forEach((key) => {
        if (nextErr[key] !== undefined) {
          nextErr[key] = '';
        }
      });
      return nextErr;
    });

    if (populatedFields.length > 0) {
      showToast(t('accommodation.toastAutoFillOk'), 'success');
    } else {
      showToast(t('accommodation.toastAutoFillNoMatch'), 'warning');
    }
  };

  const showToast = (text, type = 'info') => {
    setToast({ text, type });
    setToastOpen(true);
  };

  useEffect(() => {
    const id = browserGlobal.setTimeout(() => {
      const next = titleSearch.trim();
      const prev = debouncedTitleRef.current;
      if (prev !== next) {
        setPage(0);
      }
      setDebouncedTitle(next);
    }, 400);
    return () => browserGlobal.clearTimeout(id);
  }, [titleSearch]);

  useEffect(() => {
    setPage(0);
  }, [isActiveFilter]);

  /** Refine current page: safe if API ignores title/isActive; idempotent if API already filtered. */
  const displayedAccommodations = useMemo(() => {
    const list = accommodations?.payload || [];
    let out = Array.isArray(list) ? [...list] : [];
    const q = debouncedTitle.trim().toLowerCase();
    if (q) {
      const hay = (v) => String(v ?? '').toLowerCase();
      out = out.filter(
        (a) =>
          hay(a.title).includes(q) ||
          hay(a.Title).includes(q) ||
          hay(a.title_EN).includes(q) ||
          hay(a.title_DE).includes(q) ||
          hay(a.description).includes(q) ||
          hay(a.Description).includes(q) ||
          hay(a.description_EN).includes(q) ||
          hay(a.description_DE).includes(q) ||
          hay(a.address).includes(q) ||
          hay(a.Address).includes(q)
      );
    }
    if (isActiveFilter === 'true') {
      out = out.filter((a) => a.isActive === true || a.IsActive === true);
    } else if (isActiveFilter === 'false') {
      out = out.filter((a) => a.isActive === false || a.IsActive === false);
    }
    return out;
  }, [accommodations?.payload, debouncedTitle, isActiveFilter]);

  const [currentAccommodation, setCurrentAccommodation] = useState({
    id: '',
    title: '',
    description: '',
    cityId: '',
    address: '',
    postalCode: '',
    districtId: '',
    houseNumber: '',
    longitude: 0,
    latitude: 0,
    email: '',
    whatsAppNumber: '',
    phone: '',
    roomType: 'Private Room',
    livingArea: '',
    numberOfBeds: 1,
    numberOfBaths: 1,
    totalPerson: 1,
    rent: 0,
    deposit: 0,
    availableFrom: null,
    Pictures: [], // Stores new File objects
    existingBannerImages: [], // Stores existing image URLs
    picturesRemove: [],
    isActive: true,
    approvedBy: null,
    district: null,
    sharingImageUrl: null,
    facilities: '',
    ownerName: '',
    approvedByName: '',
    isEnabled: true,
    facilityIds: [],
    approvalStatus: ApprovalStatus.PENDING,
    anmeldungPossible: false,
    genderPreference: 'Male',
    furnishingStatus: 'Fully Furnished',
    utilitiesIncluded: false,
    commissionFree: false,
    contractProvided: false,
    spacesAvailable: 1,
  });

  const currentAccommodationRef = useRef(currentAccommodation);
  useEffect(() => {
    currentAccommodationRef.current = currentAccommodation;
  }, [currentAccommodation]);

  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const addressSearchTimerRef = useRef(null);

  const handleAddressSearch = async (query) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      return;
    }

    if (addressSearchTimerRef.current) clearTimeout(addressSearchTimerRef.current);

    addressSearchTimerRef.current = setTimeout(async () => {
      setAddressLoading(true);
      try {
        const url = `https://geocode.search.hereapi.com/v1/geocode?q=${encodeURIComponent(query)}&limit=5&in=countryCode:DEU&apiKey=6Y5UiHisZ3Ig9XwBJT20IwJMXar22qUlLWDpXYVKjw0`;
        const response = await fetch(url);
        const data = await response.json();
        setAddressSuggestions(data.items || []);
      } catch (error) {
        console.error('Error fetching address suggestions:', error);
      } finally {
        setAddressLoading(false);
      }
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (addressSearchTimerRef.current) clearTimeout(addressSearchTimerRef.current);
    };
  }, []);

  const fetchDistricts = async (cityId) => {
    try {
      const response = await districtsAPI.getDistricts(cityId, 0, 1000);
      if (response?.data?.success === true) {
        const districtsList = response.data.payload || [];
        setDistricts(districtsList);
      } else {
        setDistricts([]);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    }
  };

  const fetchFacilities = async () => {
    try {
      const response = await accommodationFacilitiesAPI.getFacilities(0, 100);
      if (response?.data?.success === true) {
        setFacilities(response.data.payload || []);
      } else {
        setFacilities([]);
      }
    } catch (error) {
      console.error('Error fetching facilities:', error);
      setFacilities([]);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await citiesAPI.getCities();
      if (response?.data?.success === true) {
        const citiesList = response.data.payload || [];
        setCities(citiesList);
        if (citiesList.length > 0) {
          setSelectedCity((prev) => (prev ? prev : citiesList[0].id));
        }
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchAccommodations = async () => {
    const cityId = selectedCity;
    if (!cityId) {
      setAccommodations({ payload: [], totalRecord: 0 });
      setTotalCount(0);
      return;
    }

    setIsLoading(true);
    try {
      const isActiveParam = getIsActiveFilterParam(isActiveFilter);
      const response = await accommodationAPI.getAccommodations(cityId, page, rowsPerPage, {
        title: debouncedTitle || undefined,
        isActive: isActiveParam,
      });

      if (response?.data?.success === true) {
        const accommodationsData = normalizeAccommodationsPayload(response.data.payload);

        setAccommodations({
          payload: accommodationsData,
          totalRecord: response.data.totalRecord || accommodationsData.length
        });
        setTotalCount(response.data.totalRecord || accommodationsData.length);
      } else {
        setAccommodations({ payload: [], totalRecord: 0 });
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching accommodations:', error);
      setAccommodations({ payload: [], totalRecord: 0 });
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (initialCitiesBootstrapRef.current) return;
    initialCitiesBootstrapRef.current = true;
    fetchCities();
    fetchFacilities();
  }, []);

  useEffect(() => {
    if (!selectedCity) {
      setDistricts([]);
      return;
    }
    fetchDistricts(selectedCity);
  }, [selectedCity]);

  useEffect(() => {
    fetchAccommodations();
  }, [selectedCity, page, rowsPerPage, debouncedTitle, isActiveFilter]);

  const openApprovalDialog = (accommodation, status) => {
    const rowActive = accommodation?.isActive === true || accommodation?.IsActive === true;
    if (!rowActive) return;
    setAccommodationToApprove(accommodation);
    setApprovalStatus(status || '');
    setApprovalNote('');
    setApprovalDialogOpen(true);
  };

  const closeApprovalDialog = () => {
    setApprovalDialogOpen(false);
    setAccommodationToApprove(null);
    setApprovalNote('');
  };

  const handleApproval = async () => {
    setIsSubmitting(true);
    try {
      await accommodationAPI.updateAccomodationApproval({
        id: accommodationToApprove.id,
        [APPROVAL_STATUS_FIELD]: approvalStatus.trim(),
        approvalNote: approvalNote.trim()
      });

      closeApprovalDialog();
      showToast(t('accommodation.toastApprovalOk'), 'success');
      fetchAccommodations();
    } catch (error) {
      console.error('Error updating approval status:', error);
      showToast(t('accommodation.toastApprovalErr') + ': ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (browserGlobal.confirm(t('accommodation.confirmDelete'))) {
      setIsSubmitting(true);
      try {
        await accommodationAPI.deleteAccommodation(id);
        showToast(t('accommodation.toastDeleted'), 'success');
        fetchAccommodations();
      } catch (error) {
        console.error('Error deleting accommodation:', error);
        showToast(error?.response?.data?.errorMessage || t('accommodation.toastDeleteError'), 'error');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleOpen = async (accommodation = null, isView = false) => {
    setViewMode(isView);
    setAutoFillText('');
    setErrors({ ...ACCOMMODATION_EMPTY_ERRORS });
    if (accommodation) {
      setIsLoading(true);
      let fullData = accommodation;
      try {
        const response = await accommodationAPI.getAccommodation(accommodation.id);
        if (response?.data?.success && response.data.payload) {
          fullData = response.data.payload;
        }
      } catch (error) {
        console.error('Error fetching accommodation details:', error);
      }
      setIsLoading(false);

      const bannerList = mergeUniqueBannerUrls(
        extractAccommodationBannerUrls(fullData),
        extractAccommodationBannerUrls(accommodation)
      );
      setCurrentAccommodation({
        ...omitAccommodationImagePayloadFields(fullData),
        availableFrom: fullData.availableFrom ? parseDateForInput(fullData.availableFrom) : '',
        facilities: fullData.facilities ? (Array.isArray(fullData.facilities) ? fullData.facilities : fullData.facilities.split(',').map(s => s.trim()).filter(Boolean)) : [],
        anmeldungPossible: fullData.anmeldungPossible ?? fullData.AnmeldungPossible ?? false,
        genderPreference: fullData.genderPreference || fullData.GenderPreference || 'Male',
        furnishingStatus: fullData.furnishingStatus || fullData.FurnishingStatus || 'Fully Furnished',
        utilitiesIncluded: fullData.utilitiesIncluded ?? fullData.UtilitiesIncluded ?? false,
        commissionFree: fullData.commissionFree ?? fullData.CommissionFree ?? false,
        contractProvided: fullData.contractProvided ?? fullData.ContractProvided ?? false,
        spacesAvailable: fullData.spacesAvailable ?? fullData.SpacesAvailable ?? 1,
        roomType: fullData.roomType || fullData.RoomType || 'Private Room',
        accommodationTypeId: fullData.accommodationTypeId || fullData.AccommodationTypeId || fullData.typeId || fullData.TypeId || accommodation?.accommodationTypeId || accommodation?.typeId || '',
        livingArea: fullData.livingArea || fullData.LivingArea || '',
        numberOfBeds: fullData.numberOfBeds ?? fullData.NumberOfBeds ?? 1,
        numberOfBaths: fullData.numberOfBaths ?? fullData.NumberOfBaths ?? 1,
        totalPerson: fullData.totalPerson ?? fullData.TotalPerson ?? fullData.totalPersons ?? fullData.TotalPersons ?? accommodation?.totalPerson ?? accommodation?.TotalPerson ?? accommodation?.totalPersons ?? accommodation?.TotalPersons ?? 1,
        phone: fullData.phone || fullData.Phone || '',
        whatYouGet: (normalizeCommaSeparated(fullData.whatYouGet || fullData.WhatYouGet || accommodation?.whatYouGet || accommodation?.WhatYouGet) || []).filter(Boolean),
        Pictures: [],
        existingBannerImages: bannerList,
        picturesRemove: []
      });
      setEditMode(true);
      // Fetch districts for the accommodation's city
      if (fullData.cityId) {
        await fetchDistricts(fullData.cityId);
      }
    } else {
      setCurrentAccommodation({
        id: '',
        title: '',
        description: '',
        address: '',
        houseNumber: '',
        postalCode: '',
        cityId: '',
        districtId: '',
        email: '',
        whatsAppNumber: '',
        phone: '',
        roomType: 'Private Room',
        accommodationTypeId: '',
        livingArea: '',
        numberOfBeds: 1,
        numberOfBaths: 1,
        totalPerson: 1,
        ownerName: '',
        rent: 0,
        deposit: 0,
        availableFrom: '',
        facilities: [],
        whatYouGet: [],
        Pictures: [],
        existingBannerImages: [],
        picturesRemove: [],
        isActive: true,
        isEnabled: true,
        approvalStatus: ApprovalStatus.PENDING,
        anmeldungPossible: false,
        genderPreference: 'Male',
        furnishingStatus: 'Fully Furnished',
        utilitiesIncluded: false,
        commissionFree: false,
        contractProvided: false,
        spacesAvailable: 1,
        latitude: '',
        longitude: '',
      });
      setEditMode(false);
    }
    setAddressSuggestions([]);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditMode(false);
    setViewMode(false);
    setAutoFillText('');
    setAddressSuggestions([]);
    setErrors({ ...ACCOMMODATION_EMPTY_ERRORS });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setCurrentAccommodation((prev) => ({
        ...prev,
        Pictures: [...prev.Pictures, ...files],
      }));
    }
  };

  const handleRemoveImage = (index, isExisting = false) => {
    setCurrentAccommodation((prev) => {
      if (isExisting) {
        const updatedExisting = [...prev.existingBannerImages];
        updatedExisting.splice(index, 1);
        return {
          ...prev,
          existingBannerImages: updatedExisting,
          picturesRemove: [
            ...(prev.picturesRemove || []),
            prev.existingBannerImages[index]
          ].filter(Boolean)
        };
      } else {
        const updatedPictures = [...prev.Pictures];
        updatedPictures.splice(index, 1);
        return { ...prev, Pictures: updatedPictures };
      }
    });
  };

  const handleAddArrayItem = (fieldName) => {
    setCurrentAccommodation((prev) => ({
      ...prev,
      [fieldName]: [...(prev[fieldName] || []), ''],
    }));
  };

  const handleRemoveArrayItem = (fieldName, index) => {
    setCurrentAccommodation((prev) => ({
      ...prev,
      [fieldName]: (prev[fieldName] || []).filter((_, i) => i !== index),
    }));
  };

  const handleArrayFieldChange = (fieldName, index, value) => {
    setCurrentAccommodation((prev) => {
      const updated = [...(prev[fieldName] || [])];
      updated[index] = value;
      return { ...prev, [fieldName]: updated };
    });
  };

  const renderArrayField = (label, fieldName) => {
    const items = currentAccommodation[fieldName] || [];
    return (
      <Grid item xs={12} data-field={fieldName}>
        <Box sx={{ mt: 1, mb: 1, p: 2, border: '1px solid #e5e7eb', borderRadius: '12px', bgcolor: '#f9fafb' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151' }}>
              {label}
            </Typography>
            {!viewMode && (
              <Button
                size="small"
                startIcon={<Add />}
                onClick={() => handleAddArrayItem(fieldName)}
                sx={{
                  color: '#0F3D3E',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: 'rgba(15, 61, 62, 0.06)' },
                }}
              >
                {t('contacts.addItem') || 'Add Item'}
              </Button>
            )}
          </Box>

          {items.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', pl: 1 }}>
              {t('contacts.noItemsAdded') || 'No items added yet. Click "Add Item" to add.'}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {items.map((item, index) => {
                const isOverLimit = (item || '').length > 200;
                return (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      maxRows={4}
                      value={item}
                      onChange={(e) => handleArrayFieldChange(fieldName, index, e.target.value)}
                      placeholder={`Enter ${label.toLowerCase()} item`}
                      error={isOverLimit}
                      InputProps={{ readOnly: viewMode }}
                      helperText={
                        isOverLimit
                          ? `${t('contacts.charLimitExceeded') || 'Character limit exceeded:'} ${(item || '').length}/200`
                          : `${(item || '').length}/200`
                      }
                      FormHelperTextProps={{
                        sx: { color: isOverLimit ? 'error.main' : 'text.secondary', textAlign: 'right' },
                      }}
                    />
                    {!viewMode && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveArrayItem(fieldName, index)}
                        sx={{ mt: 0.5 }}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Grid>
    );
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    if (name && Object.prototype.hasOwnProperty.call(ACCOMMODATION_EMPTY_ERRORS, name)) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }

    setCurrentAccommodation((prev) => ({
      ...prev,
      [name]: fieldValue
    }));

    const liveValidator = ACCOMMODATION_LIVE_FIELD_VALIDATORS[name];
    if (liveValidator) {
      const fieldError = liveValidator(fieldValue, t);
      setErrors((prev) => ({ ...prev, [name]: fieldError }));
    }
  };

  const validateAccommodationForm = () => validateAccommodationFormFields(currentAccommodation, t);

  const handleSubmit = async () => {
    const nextErrors = validateAccommodationForm();
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) {
      const firstErrorField = ACCOMMODATION_FIELD_ORDER.find((field) => nextErrors[field]);
      if (firstErrorField) {
        scrollToField(firstErrorField);
      }
      return;
    }

    const apiDate = formatAvailableFromForApi(currentAccommodation.availableFrom);

    setIsSubmitting(true);
    try {
      const formDataToSend = buildAccommodationFormData(currentAccommodation, editMode, apiDate);

      let response;
      if (editMode) {
        response = await accommodationAPI.updateAccommodation(formDataToSend);
      } else {
        response = await accommodationAPI.createAccommodation(formDataToSend);
      }

      const isSuccess =
        response?.data?.success === true ||
        response?.data?.isSuccess === true ||
        (response?.data?.success !== false &&
          response?.data?.isSuccess !== false &&
          (response?.status === 200 || response?.status === 201));

      if (isSuccess) {
        showToast(editMode ? t('accommodation.toastUpdated') : t('accommodation.toastCreated'), 'success');
        handleClose();
        fetchAccommodations();
      } else {
        const apiMsg =
          response?.data?.errorMessage ||
          response?.data?.message ||
          (typeof response?.data === 'string' ? response.data : null);
        showToast(
          apiMsg || (editMode ? t('accommodation.toastUpdateError') : t('accommodation.toastSaveError')),
          'error'
        );
      }
    } catch (error) {
      console.error('Error saving accommodation:', error);
      const apiMsg =
        error?.response?.data?.errorMessage ||
        error?.response?.data?.message ||
        (typeof error?.response?.data === 'string' ? error.response.data : null);
      showToast(
        apiMsg || `${t('accommodation.toastSaveError')}: ${error?.message || 'Unknown error'}`,
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box p={1}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={3}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 1.5, sm: 0 }}
      >
        <Typography variant="h4" color="primary">{t('accommodation.title')}</Typography>
        {canAddEdit && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {t('accommodation.add')}
          </Button>
        )}
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('accommodation.selectCity')}</InputLabel>
            <Select
              value={selectedCity}
              label={t('accommodation.selectCity')}
              onChange={(e) => {
                setSelectedCity(e.target.value);
                setPage(0);
              }}
            >
              {cities.map((city) => (
                <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="accommodation-active-filter-label" shrink>
              {t('promotions.filterStatus')}
            </InputLabel>
            <Select
              labelId="accommodation-active-filter-label"
              displayEmpty
              value={isActiveFilter}
              label={t('promotions.filterStatus')}
              renderValue={(selected) => {
                if (selected === 'true') return t('common.active');
                if (selected === 'false') return t('common.inactive');
                return (
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    {t('promotions.allStatusesHint')}
                  </Box>
                );
              }}
              onChange={(e) => {
                setIsActiveFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              <MenuItem value="true">{t('common.active')}</MenuItem>
              <MenuItem value="false">{t('common.inactive')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('promotions.searchTitle')}
            value={titleSearch}
            onChange={(e) => setTitleSearch(e.target.value)}
          />
        </Grid>
      </Grid>

      {isLoading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress sx={{ color: '#0F3D3E' }} />
        </Box>
      ) : (
        <>
          <TableContainer
            component={Paper}
            sx={{
              mt: 3,
              boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
              borderRadius: 2,
              '& .MuiTableCell-head': {
                backgroundColor: '#ebf4f4',
                color: '#0F3D3E',
                fontWeight: 600
              },
              '& .MuiTableRow-root:hover': {
                backgroundColor: '#ebf4f4'
              }
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.title')}</TableCell>
                  <TableCell>{t('common.description')}</TableCell>
                  <TableCell>{t('accommodation.colLocation')}</TableCell>
                  <TableCell>{t('accommodation.colContact')}</TableCell>
                  <TableCell>{t('accommodation.colFinancial')}</TableCell>
                  <TableCell>{t('accommodation.colAvailability')}</TableCell>
                  <TableCell>{t('accommodation.colFacilities')}</TableCell>
                  <TableCell>{t('common.status')}</TableCell>
                  <TableCell align="right">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedAccommodations.map((accommodation) => (
                  <TableRow key={accommodation.id}>
                    {/* Title */}
                    <TableCell>
                      <Typography variant="subtitle2">{accommodation.title}</Typography>
                      {(accommodation.roomType || accommodation.RoomType) && (
                        <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 0.5 }}>
                          Room: {accommodation.roomType || accommodation.RoomType}
                        </Typography>
                      )}
                      <Typography variant="caption" display="block" color="textSecondary">
                        {(accommodation.numberOfBeds ?? accommodation.NumberOfBeds ?? 0)} Beds | {(accommodation.numberOfBaths ?? accommodation.NumberOfBaths ?? 0)} Rooms
                        {(accommodation.totalPerson || accommodation.TotalPerson || accommodation.totalPersons || accommodation.TotalPersons) ? ` | ${accommodation.totalPerson || accommodation.TotalPerson || accommodation.totalPersons || accommodation.TotalPersons} Persons` : ''}
                        {(accommodation.livingArea || accommodation.LivingArea) ? ` | ${accommodation.livingArea || accommodation.LivingArea} m²` : ''}
                      </Typography>
                    </TableCell>

                    {/* Description */}
                    <TableCell>
                      <Tooltip
                        title={
                          <Box sx={{ p: 0.5 }}>
                            <Typography variant="body2">{accommodation.description || '-'}</Typography>
                          </Box>
                        }
                        placement="top"
                        arrow
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: 280 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              lineHeight: 1.4
                            }}
                          >
                            {accommodation.description || '-'}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>

                    {/* Location Details */}
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="body2">
                          {formatAccommodationAddress(accommodation.address, accommodation.houseNumber)}
                          {accommodation.postalCode && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              {accommodation.postalCode}
                            </Typography>
                          )}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip
                            label={cities.find(c => c.id === accommodation.cityId)?.name} size="small" />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Typography variant="body2">{accommodation.district}</Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Contact Info */}
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {(accommodation.email || accommodation.Email) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Email fontSize="small" color="action" />
                            <Typography variant="body2">{accommodation.email || accommodation.Email}</Typography>
                          </Box>
                        )}
                        {(accommodation.phone || accommodation.Phone) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIcon fontSize="small" color="action" />
                            <Typography variant="body2">{accommodation.phone || accommodation.Phone}</Typography>
                          </Box>
                        )}
                        {(accommodation.whatsAppNumber || accommodation.WhatsAppNumber) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WhatsApp fontSize="small" color="action" />
                            <Typography variant="body2">{accommodation.whatsAppNumber || accommodation.WhatsAppNumber}</Typography>
                          </Box>
                        )}
                        {accommodation.ownerName && (
                          <Typography variant="caption" color="textSecondary">
                            {t('accommodation.ownerPrefix')}: {accommodation.ownerName}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    {/* Financial Details */}
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="subtitle2" color="primary.main">
                          €{accommodation.rent.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {t('accommodation.depositLabel')}: €{accommodation.deposit.toLocaleString()}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Availability */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTime fontSize="small" color="action" />
                        <Typography variant="body2">{accommodation.availableFrom}</Typography>
                      </Box>
                    </TableCell>

                    {/* Facilities */}
                    <TableCell>
                      <Typography variant="body2">{accommodation.facilities}</Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Chip
                          label={getApprovalStatusLabel(accommodation.approvalStatus)}
                          color={getApprovalStatusChipColor(accommodation.approvalStatus)}
                          size="small"
                          sx={{ minWidth: 120 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {t('accommodation.isActiveLabel')}:  <Chip
                            label={accommodation.isActive ? t('common.yes') : t('common.no')}
                            color={accommodation.isActive ? 'success' : 'error'}
                            size="small"
                            variant="outlined"
                          />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {t('accommodation.isEnabledLabel')}: <Chip
                            label={accommodation.isEnabled ? t('common.yes') : t('common.no')}
                            color={accommodation.isEnabled ? 'success' : 'error'}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Actions */}
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <IconButton
                            color="info"
                            onClick={() => handleOpen(accommodation, true)}
                            size="small"
                            title={t('common.view')}
                            disabled={isSubmitting}
                          >
                            <Visibility />
                          </IconButton>
                          <IconButton
                            color="primary"
                            onClick={() => handleOpen(accommodation, false)}
                            size="small"
                            title="Edit"
                            disabled={isSubmitting}
                          >
                            <Edit />
                          </IconButton>
                          {canDelete && (
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(accommodation.id)}
                              size="small"
                              title="Delete"
                              disabled={isSubmitting}
                            >
                              <Delete />
                            </IconButton>
                          )}
                          {/* Approval status actions only when accommodation is active */}
                          <AccommodationApprovalActions
                            accommodation={accommodation}
                            isSubmitting={isSubmitting}
                            onOpenApprovalDialog={openApprovalDialog}
                          />
                        </Box>
                        {accommodation.approvalNote && (
                          <Typography
                            variant="caption"
                            color={getApprovalNoteTypographyColor(accommodation.approvalStatus)}
                            sx={{ mt: 0.5, display: 'block', maxWidth: 200, wordBreak: 'break-word' }}
                          >
                            Note: {accommodation.approvalNote}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number.parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage={t('common.rowsPerPage')}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Box>
        </>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobileView}>
        <DialogTitle>{viewMode ? t('accommodation.dialogView') : editMode ? t('accommodation.dialogEdit') : t('accommodation.dialogAdd')}</DialogTitle>
        <DialogContent ref={dialogContentRef}>
          <Box sx={{ mt: 2, '& .MuiFormLabel-asterisk': { color: 'error.main' } }}>
            {!viewMode && (
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 3,
                  borderRadius: 3,
                  border: '1px solid rgba(15, 61, 62, 0.18)',
                  background: 'linear-gradient(135deg, #f0fdfa 0%, #ebf4f4 100%)',
                  boxShadow: '0 2px 12px rgba(15, 61, 62, 0.04)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      icon={<AutoFixHigh sx={{ color: '#0F3D3E !important', fontSize: '1.1rem' }} />}
                      label={t('accommodation.autoFillTitle')}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.825rem',
                        backgroundColor: 'rgba(15, 61, 62, 0.12)',
                        color: '#0F3D3E',
                        px: 0.5,
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: '#0F3D3E', opacity: 0.85, fontWeight: 500 }}>
                    {t('accommodation.autoFillSubtitle')}
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  placeholder={t('accommodation.autoFillPlaceholder')}
                  value={autoFillText}
                  onChange={(e) => setAutoFillText(e.target.value)}
                  sx={{
                    mb: 2,
                    backgroundColor: '#ffffff',
                    borderRadius: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '& fieldset': { borderColor: 'rgba(15, 61, 62, 0.25)' },
                      '&:hover fieldset': { borderColor: '#0F3D3E' },
                      '&.Mui-focused fieldset': { borderColor: '#0F3D3E' },
                    },
                  }}
                />

                <Stack direction="row" spacing={1.5} justifyContent="flex-end" alignItems="center">
                  {autoFillText && (
                    <Button
                      size="small"
                      onClick={() => setAutoFillText('')}
                      sx={{
                        color: '#64748b',
                        fontWeight: 600,
                        '&:hover': { color: '#0F3D3E', backgroundColor: 'rgba(15, 61, 62, 0.08)' },
                      }}
                    >
                      {t('common.clear')}
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    size="medium"
                    startIcon={isAiLoading ? <CircularProgress size={18} color="inherit" /> : <AutoFixHigh />}
                    onClick={handleAutoFillFromText}
                    disabled={isAiLoading || !autoFillText.trim()}
                    sx={{
                      px: 2.5,
                      py: 0.8,
                      borderRadius: 2,
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      color: '#ffffff !important',
                      background: 'linear-gradient(135deg, #0F3D3E 0%, #1b5a5c 100%)',
                      boxShadow: '0 4px 10px rgba(15, 61, 62, 0.25)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1b5a5c 0%, #328284 100%)',
                        boxShadow: '0 6px 14px rgba(15, 61, 62, 0.35)',
                      },
                      '&.Mui-disabled': {
                        background: '#e2e8f0 !important',
                        color: '#94a3b8 !important',
                        boxShadow: 'none !important',
                        border: '1px solid #cbd5e1',
                        '& .MuiButton-startIcon': {
                          color: '#94a3b8 !important',
                        },
                      },
                    }}
                  >
                    {t('accommodation.btnAutoFill')}
                  </Button>
                </Stack>
              </Paper>
            )}
            <Grid container spacing={3}>
              <Grid item xs={12} data-field="title">
                <TextField
                  fullWidth
                  label={t('common.title')}
                  name="title"
                  value={currentAccommodation.title}
                  onChange={handleChange}
                  required
                  error={Boolean(errors.title)}
                  helperText={errors.title || undefined}
                  InputProps={{ readOnly: viewMode }}
                />
              </Grid>
              <Grid item xs={12} data-field="description">
                <TextField
                  fullWidth
                  label={t('common.description')}
                  name="description"
                  value={currentAccommodation.description}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  required
                  error={Boolean(errors.description)}
                  helperText={errors.description || undefined}
                  InputProps={{ readOnly: viewMode }}
                />
              </Grid>
              <Grid item xs={12} sm={6} data-field="cityId">
                <FormControl fullWidth required error={Boolean(errors.cityId)} sx={{ pointerEvents: viewMode ? 'none' : 'auto' }}>
                  <InputLabel id="accommodation-form-city-label">{t('common.city')}</InputLabel>
                  <Select
                    labelId="accommodation-form-city-label"
                    name="cityId"
                    value={currentAccommodation.cityId}
                    label={`${t('common.city')} *`}
                    readOnly={viewMode}
                    onChange={(e) => {
                      const newCityId = e.target.value;
                      setErrors((prev) => ({ ...prev, cityId: '' }));
                      setCurrentAccommodation(prev => ({
                        ...prev,
                        cityId: newCityId,
                        districtId: '',
                        district: ''
                      }));
                      if (newCityId) {
                        fetchDistricts(newCityId);
                      } else {
                        setDistricts([]);
                      }
                    }}
                  >
                    {cities.map(city => (
                      <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
                    ))}
                  </Select>
                  {errors.cityId ? <FormHelperText>{errors.cityId}</FormHelperText> : null}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} data-field="districtId">
                <FormControl fullWidth required disabled={!currentAccommodation.cityId} error={Boolean(errors.districtId)} sx={{ pointerEvents: viewMode ? 'none' : 'auto' }}>
                  <InputLabel id="accommodation-district-form-label">District</InputLabel>
                  <Select
                    labelId="accommodation-district-form-label"
                    name="districtId"
                    value={currentAccommodation.districtId ?? ''}
                    label="District *"
                    readOnly={viewMode}
                    onChange={(e) => {
                      const val = e.target.value;
                      setErrors((prev) => ({ ...prev, districtId: '' }));
                      setCurrentAccommodation(prev => ({
                        ...prev,
                        districtId: val === '' ? '' : val,
                        district: val === '' ? '' : (districts.find(d => d.id === Number(val))?.name || '')
                      }));
                    }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {districts.map((district) => (
                      <MenuItem key={district.id} value={district.id}>
                        {district.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.districtId ? <FormHelperText>{errors.districtId}</FormHelperText> : null}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} data-field="accommodationTypeId">
                <FormControl fullWidth required error={Boolean(errors.accommodationTypeId)} sx={{ pointerEvents: viewMode ? 'none' : 'auto' }}>
                  <InputLabel id="accommodation-form-type-label">Accommodation Type</InputLabel>
                  <Select
                    labelId="accommodation-form-type-label"
                    name="accommodationTypeId"
                    value={currentAccommodation.accommodationTypeId || currentAccommodation.typeId || ''}
                    label="Accommodation Type *"
                    readOnly={viewMode}
                    onChange={(e) => {
                      const val = e.target.value;
                      setErrors((prev) => ({ ...prev, accommodationTypeId: '' }));
                      setCurrentAccommodation(prev => ({
                        ...prev,
                        accommodationTypeId: val,
                        typeId: val
                      }));
                    }}
                  >
                    <MenuItem value="">Select Type</MenuItem>
                    {accommodationTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.accommodationTypeId ? <FormHelperText>{errors.accommodationTypeId}</FormHelperText> : null}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} data-field="facilities">
                <FormControl
                  fullWidth
                  required
                  error={Boolean(errors.facilities)}
                  sx={{ pointerEvents: viewMode ? 'none' : 'auto' }}
                >
                  <InputLabel id="accommodation-form-facilities-label">
                    {t('accommodation.fieldFacilities')}
                  </InputLabel>
                  <Select
                    labelId="accommodation-form-facilities-label"
                    name="facilities"
                    multiple
                    value={currentAccommodation.facilities || []}
                    label={`${t('accommodation.fieldFacilities')} *`}
                    readOnly={viewMode}
                    onChange={handleChange}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((val) => (
                          <Chip
                            key={val}
                            label={val}
                            size="small"
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {facilities.map((fac) => (
                      <MenuItem key={fac.id} value={fac.title_EN || fac.name_EN || fac.title || fac.name}>
                        {fac.title_EN || fac.name_EN || fac.title || fac.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.facilities ? (
                    <FormHelperText>{errors.facilities}</FormHelperText>
                  ) : null}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} data-field="address">
                <Autocomplete
                  freeSolo
                  loading={addressLoading}
                  options={addressSuggestions}
                  getOptionLabel={(option) => typeof option === 'string' ? option : option.address?.label || ''}
                  filterOptions={(x) => x}
                  value={currentAccommodation.address || ''}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      const isObj = typeof newValue === 'object';
                      const addr = isObj ? (newValue.address || {}) : {};
                      let addressStr = '';
                      if (isObj) {
                        if (addr.street) {
                          addressStr = addr.street;
                        } else {
                          addressStr = addr.label || '';
                        }
                      } else {
                        addressStr = String(newValue);
                      }
                      addressStr = addressStr.slice(0, 50);

                      const houseNumberStr = addr.houseNumber || '';
                      const postalCodeStr = addr.postalCode || '';

                      let lat = 0;
                      let lng = 0;
                      if (isObj && newValue.position) {
                        lat = newValue.position.lat;
                        lng = newValue.position.lng;
                      }
                      setCurrentAccommodation((prev) => ({
                        ...prev,
                        address: addressStr,
                        houseNumber: houseNumberStr || prev.houseNumber,
                        postalCode: postalCodeStr || prev.postalCode,
                        latitude: lat,
                        longitude: lng
                      }));

                      if (lat && lng) {
                        setErrors((prev) => ({ ...prev, address: '' }));
                      } else {
                        setErrors((prev) => ({
                          ...prev,
                          address: addressStr ? 'Please select address from auto-fetch suggestions' : (t('contacts.errFieldRequired') || 'Required'),
                        }));
                      }
                    } else {
                      setCurrentAccommodation((prev) => ({
                        ...prev,
                        address: '',
                        houseNumber: '',
                        postalCode: '',
                        latitude: 0,
                        longitude: 0
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        address: t('contacts.errFieldRequired') || 'Required',
                      }));
                    }
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    const sliced = (newInputValue || '').slice(0, 50);
                    if (reason === 'input' || reason === 'clear') {
                      setCurrentAccommodation((prev) => ({
                        ...prev,
                        address: sliced,
                        latitude: 0,
                        longitude: 0
                      }));
                    } else {
                      setCurrentAccommodation((prev) => ({
                        ...prev,
                        address: sliced
                      }));
                    }
                    handleAddressSearch(sliced);
                  }}
                  readOnly={viewMode}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('common.address') || 'Address'}
                      required
                      error={Boolean(errors.address)}
                      helperText={errors.address || undefined}
                      onBlur={(e) => {
                        if (params.inputProps && typeof params.inputProps.onBlur === 'function') {
                          params.inputProps.onBlur(e);
                        }
                        const current = currentAccommodationRef.current;
                        const addrStr = String(current?.address || '').trim();
                        const latVal = current?.latitude;
                        const lngVal = current?.longitude;

                        if (!addrStr) {
                          setErrors((prev) => ({ ...prev, address: t('contacts.errFieldRequired') || 'Required' }));
                        } else if (!latVal || !lngVal) {
                          setErrors((prev) => ({ ...prev, address: 'Please select address from auto-fetch suggestions' }));
                        } else {
                          setErrors((prev) => ({ ...prev, address: '' }));
                        }
                      }}
                      inputProps={{
                        ...params.inputProps,
                        maxLength: 50
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {addressLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} data-field="houseNumber">
                <TextField
                  fullWidth
                  label={t('common.houseNumber')}
                  name="houseNumber"
                  value={currentAccommodation.houseNumber}
                  onChange={handleChange}
                  required
                  error={Boolean(errors.houseNumber)}
                  helperText={errors.houseNumber || undefined}
                  InputProps={{ readOnly: viewMode }}
                />
              </Grid>
              <Grid item xs={12} sm={6} data-field="postalCode">
                <TextField
                  fullWidth
                  label={t('common.postalCode')}
                  name="postalCode"
                  value={currentAccommodation.postalCode}
                  onChange={handleChange}
                  required
                  error={Boolean(errors.postalCode)}
                  helperText={errors.postalCode || undefined}
                  InputProps={{ readOnly: viewMode }}
                  inputProps={{
                    maxLength: 5,
                    inputMode: 'numeric',
                    pattern: '[0-9]*'
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} data-field="email">
                <TextField
                  fullWidth
                  label={t('common.email')}
                  name="email"
                  type="email"
                  value={currentAccommodation.email}
                  onChange={handleChange}
                  error={Boolean(errors.email)}
                  helperText={errors.email || undefined}
                  InputProps={{ readOnly: viewMode }}
                />
              </Grid>
              <Grid item xs={12} sm={6} data-field="whatsAppNumber">
                <TextField
                  fullWidth
                  label={t('accommodation.fieldWhatsApp')}
                  name="whatsAppNumber"
                  value={currentAccommodation.whatsAppNumber}
                  onChange={handleChange}
                  InputProps={{ readOnly: viewMode }}
                />
              </Grid>
              <Grid item xs={12} sm={6} data-field="phone">
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={currentAccommodation.phone || ''}
                  onChange={handleChange}
                  error={Boolean(errors.phone)}
                  helperText={errors.phone || undefined}
                  InputProps={{ readOnly: viewMode }}
                />
              </Grid>
              <Grid item xs={12} sm={6} data-field="availableFrom">
                <TextField
                  fullWidth
                  label={t('accommodation.fieldAvailableFrom')}
                  name="availableFrom"
                  type="date"
                  value={currentAccommodation.availableFrom || ''}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  required
                  error={Boolean(errors.availableFrom)}
                  helperText={errors.availableFrom || undefined}
                  InputProps={{ readOnly: viewMode }}
                  inputProps={{
                    min: (() => {
                      const today = new Date();
                      const yyyy = today.getFullYear();
                      const mm = String(today.getMonth() + 1).padStart(2, '0');
                      const dd = String(today.getDate()).padStart(2, '0');
                      return `${yyyy}-${mm}-${dd}`;
                    })()
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} data-field="roomType">
                <FormControl fullWidth sx={{ pointerEvents: viewMode ? 'none' : 'auto' }}>
                  <InputLabel id="accommodation-form-room-type-label">Room Type</InputLabel>
                  <Select
                    labelId="accommodation-form-room-type-label"
                    name="roomType"
                    value={currentAccommodation.roomType || 'Private Room'}
                    label="Room Type"
                    readOnly={viewMode}
                    onChange={handleChange}
                  >
                    <MenuItem value="Private Room">Private Room</MenuItem>
                    <MenuItem value="Sharing">Sharing</MenuItem>
                    {currentAccommodation.roomType &&
                      !['Private Room', 'Sharing'].includes(currentAccommodation.roomType) && (
                        <MenuItem value={currentAccommodation.roomType}>
                          {currentAccommodation.roomType}
                        </MenuItem>
                      )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} data-field="spacesAvailable">
                <TextField
                  fullWidth
                  label={getSpacesAvailableLabel(currentAccommodation.roomType || 'Private Room')}
                  name="spacesAvailable"
                  type="number"
                  value={currentAccommodation.spacesAvailable ?? 1}
                  onChange={handleChange}
                  InputProps={{ readOnly: viewMode }}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              {currentAccommodation.roomType === 'Sharing' && (
                <Grid item xs={12} sm={6} data-field="totalPerson">
                  <TextField
                    fullWidth
                    label="Total Person"
                    name="totalPerson"
                    type="number"
                    value={currentAccommodation.totalPerson ?? 1}
                    onChange={handleChange}
                    required
                    error={Boolean(errors.totalPerson)}
                    helperText={errors.totalPerson || undefined}
                    InputProps={{ readOnly: viewMode }}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
              )}
              {/* Latitude and Longitude are automatically determined from the address autocomplete selection */}
              <Grid item xs={12} sm={6} data-field="rent">
                <TextField
                  fullWidth
                  label={t('accommodation.fieldRent')}
                  name="rent"
                  type="number"
                  value={currentAccommodation.rent}
                  onChange={handleChange}
                  required
                  error={Boolean(errors.rent)}
                  helperText={errors.rent || undefined}
                  InputProps={{ readOnly: viewMode }}
                  inputProps={{
                    min: 0,
                    step: 'any'
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} data-field="deposit">
                <TextField
                  fullWidth
                  label={t('accommodation.fieldDeposit')}
                  name="deposit"
                  type="number"
                  value={currentAccommodation.deposit}
                  onChange={handleChange}
                  required
                  error={Boolean(errors.deposit)}
                  helperText={errors.deposit || undefined}
                  InputProps={{ readOnly: viewMode }}
                  inputProps={{
                    min: 0,
                    step: 'any'
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} data-field="livingArea">
                <TextField
                  fullWidth
                  label="Living Area (m²)"
                  name="livingArea"
                  type="number"
                  value={currentAccommodation.livingArea ?? ''}
                  onChange={handleChange}
                  required
                  error={Boolean(errors.livingArea)}
                  helperText={errors.livingArea || undefined}
                  InputProps={{ readOnly: viewMode }}
                  inputProps={{ min: 0, step: 'any' }}
                />
              </Grid>
              <Grid item xs={12} sm={6} data-field="numberOfBaths">
                <TextField
                  fullWidth
                  label="Total Rooms"
                  name="numberOfBaths"
                  type="number"
                  value={currentAccommodation.numberOfBaths ?? 1}
                  onChange={handleChange}
                  required
                  error={Boolean(errors.numberOfBaths)}
                  helperText={errors.numberOfBaths || undefined}
                  InputProps={{ readOnly: viewMode }}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              {currentAccommodation.roomType !== 'Sharing' && (
                <Grid item xs={12} sm={6} data-field="totalPerson">
                  <TextField
                    fullWidth
                    label="Total Person"
                    name="totalPerson"
                    type="number"
                    value={currentAccommodation.totalPerson ?? 1}
                    onChange={handleChange}
                    required
                    error={Boolean(errors.totalPerson)}
                    helperText={errors.totalPerson || undefined}
                    InputProps={{ readOnly: viewMode }}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
              )}
              {currentAccommodation.roomType === 'Sharing' && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={{ pointerEvents: viewMode ? 'none' : 'auto' }}>
                    <InputLabel id="accommodation-form-gender-pref-label">Gender Preference</InputLabel>
                    <Select
                      labelId="accommodation-form-gender-pref-label"
                      name="genderPreference"
                      value={currentAccommodation.genderPreference || 'Male'}
                      label="Gender Preference"
                      readOnly={viewMode}
                      onChange={handleChange}
                    >
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={{ pointerEvents: viewMode ? 'none' : 'auto' }}>
                  <InputLabel id="accommodation-form-furnishing-label">Furnishing Status</InputLabel>
                  <Select
                    labelId="accommodation-form-furnishing-label"
                    name="furnishingStatus"
                    value={currentAccommodation.furnishingStatus || 'Fully Furnished'}
                    label="Furnishing Status"
                    readOnly={viewMode}
                    onChange={handleChange}
                  >
                    <MenuItem value="Fully Furnished">Fully Furnished</MenuItem>
                    <MenuItem value="Semi-Furnished">Semi-Furnished</MenuItem>
                    <MenuItem value="Unfurnished">Unfurnished</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', bgcolor: '#F9FAFB' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#374151' }}>
                    Accommodation Features & Options
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(currentAccommodation.anmeldungPossible)}
                            onChange={handleChange}
                            name="anmeldungPossible"
                            disabled={viewMode}
                            color="primary"
                          />
                        }
                        label="Anmeldung Possible"
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(currentAccommodation.utilitiesIncluded)}
                            onChange={handleChange}
                            name="utilitiesIncluded"
                            disabled={viewMode}
                            color="primary"
                          />
                        }
                        label="Utilities Included"
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(currentAccommodation.commissionFree)}
                            onChange={handleChange}
                            name="commissionFree"
                            disabled={viewMode}
                            color="primary"
                          />
                        }
                        label="Commission Free"
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(currentAccommodation.contractProvided)}
                            onChange={handleChange}
                            name="contractProvided"
                            disabled={viewMode}
                            color="primary"
                          />
                        }
                        label="Contract Provided"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {renderArrayField(t('accommodation.fieldWhatYouGet') || 'What You Get', 'whatYouGet')}

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mt: 1, mb: 1 }}>
                  Banner Image
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: '#6b7280', mb: 1 }}>
                  {editMode
                    ? 'Saved photos appear below. Use + to add more; both are included when you update.'
                    : 'Add one or more images.'}
                </Typography>

                <Box sx={{
                  p: 2,
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  bgcolor: '#f9fafb',
                  minHeight: '100px'
                }}>
                  <Grid container spacing={2}>
                    {/* Existing Images */}
                    {currentAccommodation.existingBannerImages.map((url, idx) => (
                      <Grid item key={`existing-${url}-${idx}`}>
                        <Box sx={{
                          position: 'relative',
                          width: 80,
                          height: 80,
                          '&:hover .delete-btn': { opacity: 1 }
                        }}>
                          <img
                            src={url}
                            alt=""
                            referrerPolicy="no-referrer"
                            loading="lazy"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '12px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          />
                          {!viewMode && (
                            <IconButton
                              size="small"
                              className="delete-btn"
                              onClick={() => handleRemoveImage(idx, true)}
                              sx={{
                                position: 'absolute',
                                top: -8,
                                right: -8,
                                bgcolor: '#fff',
                                opacity: 0.8,
                                transition: 'all 0.2s',
                                '&:hover': { bgcolor: '#ef4444', color: '#fff', opacity: 1 },
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                p: 0.5
                              }}
                            >
                              <Close sx={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                        </Box>
                      </Grid>
                    ))}

                    {/* New Selected Images */}
                    {currentAccommodation.Pictures.map((file, idx) => (
                      <Grid item key={`new-${idx}`}>
                        <Box sx={{
                          position: 'relative',
                          width: 80,
                          height: 80,
                          '&:hover .delete-btn': { opacity: 1 }
                        }}>
                          <img
                            src={URL.createObjectURL(file)}
                            alt="New"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '12px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          />
                          {!viewMode && (
                            <IconButton
                              size="small"
                              className="delete-btn"
                              onClick={() => handleRemoveImage(idx)}
                              sx={{
                                position: 'absolute',
                                top: -8,
                                right: -8,
                                bgcolor: '#fff',
                                opacity: 0.8,
                                transition: 'all 0.2s',
                                '&:hover': { bgcolor: '#ef4444', color: '#fff', opacity: 1 },
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                p: 0.5
                              }}
                            >
                              <Close sx={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                        </Box>
                      </Grid>
                    ))}

                    {/* Add Image Button */}
                    {!viewMode && (
                      <Grid item>
                        <Button
                          component="label"
                          sx={{
                            width: 80,
                            height: 80,
                            border: '2px dashed #d1d5db',
                            borderRadius: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#6b7280',
                            textTransform: 'none',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: '#0F3D3E',
                              color: '#0F3D3E',
                              bgcolor: 'rgba(124, 58, 237, 0.04)',
                              transform: 'translateY(-2px)'
                            }
                          }}
                        >
                          <AddPhotoAlternate sx={{ fontSize: 28 }} />
                          <Typography variant="caption" sx={{ mt: 1, fontWeight: 500 }}>{t('common.add') || 'Add'}</Typography>
                          <input
                            type="file"
                            hidden
                            multiple
                            accept="image/*"
                            onChange={handleFileChange}
                          />
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" spacing={4} sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={currentAccommodation.isActive}
                        onChange={(e) => handleChange({
                          target: {
                            name: 'isActive',
                            value: e.target.checked
                          }
                        })}
                        sx={{
                          pointerEvents: viewMode ? 'none' : 'auto',
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#0F3D3E' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#0F3D3E' }
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {t('common.active')}
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={currentAccommodation.isEnabled}
                        onChange={(e) => handleChange({
                          target: {
                            name: 'isEnabled',
                            value: e.target.checked
                          }
                        })}
                        sx={{
                          pointerEvents: viewMode ? 'none' : 'auto',
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#0F3D3E' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#0F3D3E' }
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {t('accommodation.fieldEnabled')}
                      </Typography>
                    }
                  />
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{viewMode ? t('common.close') || 'Close' : t('common.cancel')}</Button>
          {!viewMode && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {getAccommodationSubmitLabel(isSubmitting, editMode, t)}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onClose={closeApprovalDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getApprovalDialogIcon(approvalStatus)}
            <Typography>
              {getApprovalDialogTitlePrefix(approvalStatus, t)} {t('accommodation.approveAccommodation')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>{t('common.status')}</InputLabel>
              <Select
                value={approvalStatus}
                label={t('common.status')}
                onChange={(e) => setApprovalStatus(e.target.value)}
              >
                <MenuItem value={ApprovalStatus.APPROVED}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle color="success" />
                    <span>{ApprovalStatusLabels[ApprovalStatus.APPROVED]}</span>
                  </Box>
                </MenuItem>
                <MenuItem value={ApprovalStatus.DECLINE}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Cancel color="error" />
                    <span>{ApprovalStatusLabels[ApprovalStatus.DECLINE]}</span>
                  </Box>
                </MenuItem>
                <MenuItem value={ApprovalStatus.PENDING}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HourglassEmpty color="warning" />
                    <span>{ApprovalStatusLabels[ApprovalStatus.PENDING]}</span>
                  </Box>
                </MenuItem>
                <MenuItem value={ApprovalStatus.INACTIVE}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Cancel color="disabled" />
                    <span>{ApprovalStatusLabels[ApprovalStatus.INACTIVE]}</span>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label={getApprovalNoteFieldLabel(approvalStatus, t)}
              name="approvalNote"
              value={approvalNote}
              onChange={(e) => setApprovalNote(e.target.value)}
              multiline
              rows={4}
              required={isDeclinedApprovalStatus(approvalStatus)}
              error={isDeclinedApprovalStatus(approvalStatus) && !approvalNote.trim()}
              helperText={isDeclinedApprovalStatus(approvalStatus) && !approvalNote.trim() ? t('accommodation.helperRejectNote') : ''}
              placeholder={getApprovalNotePlaceholder(approvalStatus, t)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApprovalDialog}>{t('common.cancel')}</Button>
          <Button
            onClick={handleApproval}
            variant="contained"
            color={getApprovalDialogButtonColor(approvalStatus)}
            disabled={isSubmitting || (isDeclinedApprovalStatus(approvalStatus) && !approvalNote.trim())}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? t('common.updating') : t('promotions.updateStatus')}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity={toast.type} sx={{ width: '100%' }}>
          {toast.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Accommodation;